import { useEffect, useRef, useState } from 'react';

import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';

import { CLAIM_TYPES_CONFIGURATION } from '../../../../Types';
import useOrganization from '../../../OrganizationContext';

const useLobOptions = ({ subOrganizationIds, isAllLobs = false, filterLobsFunc = () => true }) => {
  const [lobOptions, setLobOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const { fetchLobs } = useOrganization();
  const { lobConfigurationsDict, isLoading: isLobConfigurationsLoading } = useLobConfiguration();

  const fetchLobsRef = useRef(fetchLobs);
  fetchLobsRef.current = fetchLobs;
  const filterLobsFuncRef = useRef(filterLobsFunc);
  filterLobsFuncRef.current = filterLobsFunc;
  const lobConfigurationsDictRef = useRef(lobConfigurationsDict);
  lobConfigurationsDictRef.current = lobConfigurationsDict;
  // Pass the IDs through unchanged. They are numeric DB ids for an existing org but string
  // external_ids in the New Org Wizard's UI-only context; coercing them to numbers turns
  // external_ids into NaN, which never matches in fetchLobs (NGTPA-24531). subOrgsKey below
  // is only the effect's change-detector, never the data source.
  const subOrganizationIdsRef = useRef(subOrganizationIds);
  subOrganizationIdsRef.current = subOrganizationIds;

  const subOrgsKey = subOrganizationIds?.join(',') ?? 'null';

  useEffect(() => {
    // This is just a safety measure - isLobConfigurationsLoading will most likely never be true in this context
    if (isAllLobs && isLobConfigurationsLoading) {
      setIsLoading(true);
      return;
    }

    let cancelled = false;

    (async () => {
      setIsLoading(true);
      try {
        const allLobs = [...Object.keys(CLAIM_TYPES_CONFIGURATION), ...Object.keys(lobConfigurationsDictRef.current)];
        const data = isAllLobs ? allLobs : (await fetchLobsRef.current(subOrganizationIdsRef.current)).data;
        if (!cancelled) {
          setLobOptions(data?.filter(filterLobsFuncRef.current));
          setIsLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setIsError(true);
        }
      }
      if (!cancelled) {
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [subOrgsKey, isAllLobs, isLobConfigurationsLoading]);

  return { lobOptions, isLoading, isLoaded, isError };
};

export default useLobOptions;
